import React, { useEffect, useState, useCallback } from "react";
import PartnerLayout, { tenantSlugFor, usePartnerActiveTenant } from "@/components/PartnerLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Key, Webhook, BookOpen, Plus, Copy, Check, Trash2, RotateCcw,
  RefreshCw, Play, Eye, EyeOff, X, AlertCircle, CheckCircle2, Clock, Zap,
  ExternalLink,
} from "lucide-react";

const AVAILABLE_SCOPES = [
  { v: "read:users", g: "Read", desc: "Leer usuarios del tenant" },
  { v: "read:enrollments", g: "Read", desc: "Leer enrollments" },
  { v: "read:orders", g: "Read", desc: "Leer órdenes" },
  { v: "read:subscriptions", g: "Read", desc: "Leer suscripciones" },
  { v: "read:programs", g: "Read", desc: "Leer catálogo de programas" },
  { v: "read:cohorts", g: "Read", desc: "Leer cohortes" },
  { v: "write:users", g: "Write", desc: "Crear/actualizar usuarios" },
  { v: "write:enrollments", g: "Write", desc: "Crear enrollments (asignar alumnos)" },
  { v: "webhook:manage", g: "Webhook", desc: "Gestionar webhook endpoints" },
  { v: "webhook:read", g: "Webhook", desc: "Ver deliveries" },
  { v: "admin:audit-log", g: "Admin", desc: "Ver audit log" },
];

export default function PartnerIntegraciones() {
  const { user } = useAuth();
  const activeSlug = usePartnerActiveTenant(user);
  const slug = activeSlug || tenantSlugFor(user);
  const [tab, setTab] = useState("api-keys");

  return (
    <PartnerLayout user={user}>
      <div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.3 · Integraciones enterprise</p>
          <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
            <Zap className="w-7 h-7" /> Integraciones
          </h1>
          <p className="text-sm text-gray-400 mt-1 font-mono">/t/{slug}</p>
        </div>
        <div className="border-b border-gray-800 mb-6 flex gap-4">
          {[
            { v: "api-keys", l: "API Keys", i: Key },
            { v: "webhooks", l: "Webhooks", i: Webhook },
            { v: "docs", l: "Documentación", i: BookOpen },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
              data-testid={`integraciones-tab-${t.v}`}
              className={`py-3 px-1 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
                tab === t.v ? "border-brand-orange-600 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
              }`}>
              <t.i className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>
        {tab === "api-keys" && <ApiKeysTab tenantSlug={slug} />}
        {tab === "webhooks" && <WebhooksTab tenantSlug={slug} />}
        {tab === "docs" && <DocsTab />}
      </div>
    </PartnerLayout>
  );
}

// ================= API KEYS TAB =================
function ApiKeysTab({ tenantSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [plaintextResult, setPlaintextResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/api-keys");
      setItems(data.items || []);
    } catch (e) { toast.error("Error al cargar keys"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rotate = async (id) => {
    if (!window.confirm("¿Rotar esta key? El key anterior seguirá válido 24h como grace period.")) return;
    try {
      const { data } = await api.post(`/partner/api-keys/${id}/rotate`);
      setPlaintextResult({ ...data, action: "rotated" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta key inmediatamente? Todas las requests futuras fallarán con 401.")) return;
    try {
      await api.delete(`/partner/api-keys/${id}`);
      toast.success("Key revocada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{items.length} keys · gestión de acceso a la API v1</p>
        <button onClick={() => setShowNew(true)}
          data-testid="api-key-new-btn"
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Nueva API key
        </button>
      </div>
      <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="api-keys-table">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Sin keys. Crea la primera para conectar tu ERP/BI.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#0B0F14] border-b border-gray-800">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Nombre</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Prefix</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Scopes</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Uso 30d</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Last used</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Rate</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {items.map((k) => (
                <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <td className="px-4 py-3 text-white text-sm">{k.name}</td>
                  <td className="px-4 py-3 text-xs font-mono text-brand-orange-500">{k.key_prefix}<span className="text-gray-600">…</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-md">
                      {(k.scopes || []).slice(0, 4).map((s) => (
                        <span key={s} className="inline-flex px-1.5 py-0.5 text-[9px] font-mono rounded border border-gray-700 text-gray-400 bg-gray-800/40">{s}</span>
                      ))}
                      {(k.scopes || []).length > 4 && (
                        <span className="text-[9px] text-gray-500">+{k.scopes.length - 4}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white text-sm font-mono">{k.requests_last_30d?.toLocaleString() || 0}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-300 font-mono">{k.rate_limit_per_min}/min</td>
                  <td className="px-4 py-3">
                    {k.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                        <CheckCircle2 className="w-3 h-3" /> Activa
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-red-500/20 text-red-300 border-red-500/40">
                        Revocada
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => rotate(k.id)} title="Rotar (24h grace)"
                        data-testid={`api-key-rotate-${k.id}`}
                        className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded"><RotateCcw className="w-4 h-4" /></button>
                      {k.is_active && (
                        <button onClick={() => revoke(k.id)} title="Revocar"
                          data-testid={`api-key-revoke-${k.id}`}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded"><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewApiKeyModal onClose={() => setShowNew(false)} onCreated={(r) => { setShowNew(false); setPlaintextResult({ ...r, action: "created" }); load(); }} />}
      {plaintextResult && <PlaintextModal data={plaintextResult} onClose={() => setPlaintextResult(null)} type="api-key" />}
    </div>
  );
}

function NewApiKeyModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", scopes: [], rate_limit_per_min: 120, ip_allowlist: "" });
  const [submitting, setSubmitting] = useState(false);
  const toggleScope = (s) => {
    const has = form.scopes.includes(s);
    setForm({ ...form, scopes: has ? form.scopes.filter((x) => x !== s) : [...form.scopes, s] });
  };
  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const { data } = await api.post("/partner/api-keys", {
        name: form.name, scopes: form.scopes,
        rate_limit_per_min: parseInt(form.rate_limit_per_min) || 120,
        ip_allowlist: form.ip_allowlist ? form.ip_allowlist.split(",").map(s => s.trim()).filter(Boolean) : [],
      });
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 my-8">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display">Nueva API key</h3>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Nombre *</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="api-key-form-name"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" placeholder="ERP Nomina Trafalgar" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Scopes *</label>
          <div className="mt-2 space-y-1 max-h-56 overflow-y-auto" data-testid="api-key-form-scopes">
            {["Read", "Write", "Webhook", "Admin"].map((g) => (
              <div key={g}>
                <p className="text-[10px] text-gray-500 uppercase font-semibold mt-2">{g}</p>
                {AVAILABLE_SCOPES.filter((s) => s.g === g).map((s) => (
                  <label key={s.v} className="flex items-center gap-2 py-1 px-2 hover:bg-[#0B0F14] rounded cursor-pointer">
                    <input type="checkbox" checked={form.scopes.includes(s.v)} onChange={() => toggleScope(s.v)}
                      data-testid={`api-key-scope-${s.v}`}
                      className="w-4 h-4 accent-brand-orange-600" />
                    <span className="text-xs font-mono text-white flex-1">{s.v}</span>
                    <span className="text-[10px] text-gray-500">{s.desc}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-400">Rate limit / min</label>
            <input type="number" min="10" max="6000" value={form.rate_limit_per_min}
              onChange={(e) => setForm({ ...form, rate_limit_per_min: e.target.value })}
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-400">IP allowlist (CSV)</label>
            <input value={form.ip_allowlist} onChange={(e) => setForm({ ...form, ip_allowlist: e.target.value })}
              placeholder="10.0.0.0/24,203.0.113.5"
              className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-xs text-white font-mono" />
          </div>
        </div>
        <button type="submit" disabled={submitting || !form.name || form.scopes.length === 0}
          data-testid="api-key-form-submit"
          className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
          {submitting ? "Creando…" : "Crear API key"}
        </button>
      </form>
    </div>
  );
}

function PlaintextModal({ data, onClose, type }) {
  const [copied, setCopied] = useState(false);
  const [visible, setVisible] = useState(false);
  const value = type === "api-key" ? data.key : data.secret;
  const label = type === "api-key" ? "API Key" : "Webhook Secret";
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#111827] border-2 border-brand-orange-600 rounded-xl p-6 space-y-4"
        data-testid="api-key-plaintext-modal">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white font-display flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-brand-orange-500" /> {label} generado
            </h3>
            <p className="text-xs text-gray-400 mt-1">Guarda este valor. NO se volverá a mostrar.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div className="p-4 bg-[#0B0F14] border border-brand-orange-600/40 rounded-lg font-mono text-sm break-all">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase text-gray-500 tracking-widest">{type === "api-key" ? "Key plaintext" : "Secret plaintext"}</span>
            <button onClick={() => setVisible(!visible)} className="ml-auto p-1 text-gray-400 hover:text-white">
              {visible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            </button>
          </div>
          <div className="text-brand-orange-500 select-all">
            {visible ? value : value.slice(0, 12) + "•".repeat(Math.max(0, value.length - 12))}
          </div>
        </div>
        <button onClick={copy} data-testid="api-key-copy-btn"
          className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold inline-flex items-center justify-center gap-2">
          {copied ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar al portapapeles</>}
        </button>
        {data.grace_until && (
          <p className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/40 rounded p-2">
            El key anterior seguirá válido hasta <span className="font-mono">{new Date(data.grace_until).toLocaleString()}</span> como grace period de rotación.
          </p>
        )}
        <button onClick={onClose} className="w-full py-2 rounded-md border border-gray-700 text-gray-400 hover:text-white text-sm">
          He copiado la key, cerrar
        </button>
      </div>
    </div>
  );
}

// ================= WEBHOOKS TAB =================
function WebhooksTab({ tenantSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [plaintextResult, setPlaintextResult] = useState(null);
  const [pingResult, setPingResult] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/partner/webhooks");
      setItems(data.items || []);
    } catch (e) { toast.error("Error al cargar webhooks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ping = async (id) => {
    try {
      const { data } = await api.post(`/partner/webhooks/${id}/ping`);
      setPingResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rotateSecret = async (id) => {
    if (!window.confirm("¿Rotar el secret? El anterior dejará de funcionar inmediatamente.")) return;
    try {
      const { data } = await api.post(`/partner/webhooks/${id}/rotate-secret`);
      setPlaintextResult({ secret: data.secret, action: "rotated" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este webhook endpoint? (soft-delete)")) return;
    try { await api.delete(`/partner/webhooks/${id}`); toast.success("Endpoint desactivado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{items.length} endpoints · recibe eventos del portal en tiempo real</p>
        <button onClick={() => setShowNew(true)}
          data-testid="webhook-new-btn"
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Nuevo webhook
        </button>
      </div>
      <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="webhooks-table">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">Sin endpoints. Crea uno para recibir eventos.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#0B0F14] border-b border-gray-800">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">URL</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Eventos</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Last success</th>
                <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Fallas 24h</th>
                <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {items.map((w) => (
                <tr key={w.id} data-testid={`webhook-row-${w.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-xs font-mono text-white break-all max-w-md">{w.url}</div>
                    {w.description && <div className="text-[10px] text-gray-500 mt-0.5">{w.description}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(w.event_types || []).slice(0, 3).map((e) => (
                        <span key={e} className="inline-flex px-1.5 py-0.5 text-[9px] font-mono rounded border border-brand-orange-500/40 text-brand-orange-500 bg-brand-orange-500/10">{e}</span>
                      ))}
                      {(w.event_types || []).length > 3 && (
                        <span className="text-[9px] text-gray-500">+{w.event_types.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {w.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-emerald-500/20 text-emerald-300 border-emerald-500/40">
                        <CheckCircle2 className="w-3 h-3" /> Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-gray-700/40 text-gray-400 border-gray-700">Inactivo</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                    {w.last_success_at ? new Date(w.last_success_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {w.failure_count_last_24h > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border bg-red-500/20 text-red-300 border-red-500/40 font-mono font-bold">
                        {w.failure_count_last_24h}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500 font-mono">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => ping(w.id)} title="Ping test"
                        data-testid={`webhook-ping-btn-${w.id}`}
                        className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded"><Play className="w-4 h-4" /></button>
                      <button onClick={() => setDetailId(w.id)} title="Ver deliveries"
                        data-testid={`webhook-deliveries-btn-${w.id}`}
                        className="p-1.5 text-gray-400 hover:text-brand-orange-500 rounded"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => rotateSecret(w.id)} title="Rotar secret"
                        className="p-1.5 text-gray-400 hover:text-yellow-500 rounded"><RotateCcw className="w-4 h-4" /></button>
                      <button onClick={() => del(w.id)} title="Eliminar"
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNew && <NewWebhookModal onClose={() => setShowNew(false)} onCreated={(r) => { setShowNew(false); setPlaintextResult(r); load(); }} />}
      {plaintextResult && <PlaintextModal data={plaintextResult} onClose={() => setPlaintextResult(null)} type="webhook" />}
      {pingResult && <PingResultModal data={pingResult} onClose={() => setPingResult(null)} />}
      {detailId && <DeliveriesModal endpointId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function NewWebhookModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ url: "", description: "", event_types: [] });
  const [available, setAvailable] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { api.get("/partner/webhooks/available-events").then((r) => setAvailable(r.data.events || [])); }, []);
  const toggle = (e) => {
    const has = form.event_types.includes(e);
    setForm({ ...form, event_types: has ? form.event_types.filter((x) => x !== e) : [...form.event_types, e] });
  };
  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      const { data } = await api.post("/partner/webhooks", form);
      onCreated(data);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 my-8">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display">Nuevo webhook endpoint</h3>
          <button onClick={onClose} type="button" className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">URL (HTTPS) *</label>
          <input required type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            data-testid="webhook-form-url"
            placeholder="https://api.partner.mx/webhooks/dcc"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Descripción</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="ERP producción · Nomina"
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Eventos suscritos *</label>
          <div className="mt-2 space-y-1 max-h-56 overflow-y-auto" data-testid="webhook-form-events">
            {available.map((ev) => (
              <label key={ev.event_type} className="flex items-center gap-2 py-1 px-2 hover:bg-[#0B0F14] rounded cursor-pointer">
                <input type="checkbox" checked={form.event_types.includes(ev.event_type)} onChange={() => toggle(ev.event_type)}
                  data-testid={`webhook-event-${ev.event_type}`}
                  className="w-4 h-4 accent-brand-orange-600" />
                <span className="text-xs font-mono text-brand-orange-500">{ev.event_type}</span>
                <span className="text-[10px] text-gray-500 truncate">{ev.description}</span>
              </label>
            ))}
          </div>
        </div>
        <button type="submit" disabled={submitting || !form.url || form.event_types.length === 0}
          data-testid="webhook-form-submit"
          className="w-full py-2.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold disabled:opacity-60">
          {submitting ? "Creando…" : "Crear endpoint"}
        </button>
      </form>
    </div>
  );
}

function PingResultModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3"
        data-testid="webhook-ping-result">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display flex items-center gap-2">
            <Zap className="w-5 h-5" /> Ping test result
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div className={`p-3 rounded-lg border ${data.ok ? "bg-emerald-500/10 border-emerald-500/40" : "bg-red-500/10 border-red-500/40"}`}>
          <div className={`text-sm font-bold ${data.ok ? "text-emerald-300" : "text-red-300"}`}>
            {data.ok ? "✓ Delivery success" : "✗ Delivery failed"}
          </div>
          <div className="text-xs text-gray-300 font-mono mt-1">
            HTTP {data.http_status} · {data.duration_ms}ms
          </div>
        </div>
        <div className="space-y-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Signature enviada</p>
            <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded text-[10px] font-mono text-brand-orange-500 break-all">{data.signature}</div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Response body (500 chars)</p>
            <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded text-xs font-mono text-gray-300 max-h-32 overflow-y-auto">{data.response_snippet || "(vacío)"}</div>
          </div>
        </div>
        <button onClick={onClose} className="w-full py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">Cerrar</button>
      </div>
    </div>
  );
}

function DeliveriesModal({ endpointId, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ status: "", event_type: "" });
  const [detail, setDetail] = useState(null);
  const load = useCallback(async () => {
    const params = new URLSearchParams({ limit: "50" });
    if (filter.status) params.set("status", filter.status);
    if (filter.event_type) params.set("event_type", filter.event_type);
    const { data } = await api.get(`/partner/webhooks/${endpointId}/deliveries?${params.toString()}`);
    setItems(data.items || []); setTotal(data.total || 0);
  }, [endpointId, filter]);
  useEffect(() => { load(); }, [load]);
  const retry = async (id) => {
    try { await api.post(`/partner/webhooks/deliveries/${id}/retry`); toast.success("Retry encolado"); setTimeout(load, 1500); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openDetail = async (id) => {
    const { data } = await api.get(`/partner/webhooks/${endpointId}/deliveries/${id}`);
    setDetail(data);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl bg-[#111827] border border-gray-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="webhook-deliveries-modal">
        <div className="p-6 border-b border-gray-800 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white font-display">Deliveries · {total}</h3>
            <div className="mt-2 flex gap-2">
              {["", "success", "failed", "retrying", "dead_letter"].map((s) => (
                <button key={s || "all"} onClick={() => setFilter({ ...filter, status: s })}
                  data-testid={`deliveries-filter-${s || "all"}`}
                  className={`px-2 py-1 text-xs rounded border ${filter.status === s ? "bg-brand-orange-600 text-white border-brand-orange-600" : "border-gray-700 text-gray-400"}`}>
                  {s || "todos"}
                </button>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full" data-testid="webhook-deliveries-table">
            <thead className="bg-[#0B0F14] border-b border-gray-800">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Fecha</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Event</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Status</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">HTTP</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Attempts</th>
                <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Duration</th>
                <th className="text-right px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-[#0B0F14] cursor-pointer" onClick={() => openDetail(i.id)}
                  data-testid={`delivery-row-${i.id}`}>
                  <td className="px-3 py-2 text-[10px] text-gray-400 font-mono whitespace-nowrap">{(i.created_at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-xs font-mono text-brand-orange-500">{i.event_type}</td>
                  <td className="px-3 py-2"><DeliveryStatusBadge status={i.status} /></td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-300">{i.http_status_last || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-300">{i.attempts}</td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-300">{i.duration_ms_last ? i.duration_ms_last + "ms" : "—"}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    {i.status !== "success" && (
                      <button onClick={() => retry(i.id)} title="Retry manual"
                        data-testid={`webhook-retry-btn-${i.id}`}
                        className="p-1 text-gray-400 hover:text-brand-orange-500"><RefreshCw className="w-3 h-3" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {detail && <DeliveryDetailModal delivery={detail} onClose={() => setDetail(null)} />}
      </div>
    </div>
  );
}

function DeliveryStatusBadge({ status }) {
  const cfg = {
    success: { bg: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", i: CheckCircle2 },
    failed: { bg: "bg-red-500/20 text-red-300 border-red-500/40", i: AlertCircle },
    retrying: { bg: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40", i: RefreshCw },
    pending: { bg: "bg-gray-700/40 text-gray-400 border-gray-700", i: Clock },
    dead_letter: { bg: "bg-red-900/40 text-red-400 border-red-900", i: AlertCircle },
  }[status] || { bg: "bg-gray-700/40 text-gray-400 border-gray-700", i: Clock };
  const Icon = cfg.i;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded border ${cfg.bg}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

function DeliveryDetailModal({ delivery, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="delivery-detail">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-white font-display">Delivery {delivery.id.slice(0, 8)}</h3>
            <p className="text-xs text-gray-500 font-mono mt-1">event: {delivery.event_type}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded"><div className="text-[9px] text-gray-500">STATUS</div><div className="font-bold text-white">{delivery.status}</div></div>
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded"><div className="text-[9px] text-gray-500">HTTP</div><div className="font-mono text-white">{delivery.http_status_last || "—"}</div></div>
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded"><div className="text-[9px] text-gray-500">ATTEMPTS</div><div className="font-mono text-white">{delivery.attempts}</div></div>
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded"><div className="text-[9px] text-gray-500">DURATION</div><div className="font-mono text-white">{delivery.duration_ms_last || 0}ms</div></div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Signature</p>
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded text-[10px] font-mono text-brand-orange-500 break-all">{delivery.signature || "—"}</div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Payload</p>
          <pre className="p-3 bg-[#0B0F14] border border-gray-800 rounded text-[10px] font-mono text-gray-300 overflow-x-auto">{JSON.stringify(delivery.payload, null, 2)}</pre>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Response body</p>
          <div className="p-2 bg-[#0B0F14] border border-gray-800 rounded text-xs font-mono text-gray-300 max-h-32 overflow-y-auto">{delivery.response_body_snippet || "(vacío)"}</div>
        </div>
      </div>
    </div>
  );
}

// ================= DOCS TAB =================
function DocsTab() {
  return (
    <div className="max-w-3xl space-y-6" data-testid="integraciones-docs">
      <section>
        <h2 className="text-2xl font-bold text-white font-display">API v1 · Guía rápida</h2>
        <p className="mt-2 text-sm text-gray-400">
          Consume el portal DCC desde tu ERP/BI vía HTTPS con una API key. Autentica en cada request con
          header <code className="text-brand-orange-500 font-mono">Authorization: Bearer dcc_pk_...</code>.
          Todas las respuestas son JSON con estructura <code className="text-brand-orange-500 font-mono">{"{data:[...], pagination:{...}}"}</code>.
        </p>
      </section>

      <Section title="1 · Autenticación">
        <CodeBlock lang="bash" code={`# curl
curl https://tu-dcc.mx/api/v1/users?tenant_slug=trafalgar-sofipo \\
  -H "Authorization: Bearer dcc_pk_..."

# 401 = key inválida · 403 = scope insuficiente · 429 = rate limit`} />
      </Section>

      <Section title="2 · Endpoints principales">
        <ul className="text-sm text-gray-300 space-y-1 font-mono">
          <li><span className="text-emerald-400">GET</span> /api/v1/users — read:users</li>
          <li><span className="text-emerald-400">GET</span> /api/v1/enrollments — read:enrollments</li>
          <li><span className="text-brand-orange-500">POST</span> /api/v1/enrollments — write:enrollments</li>
          <li><span className="text-emerald-400">GET</span> /api/v1/orders — read:orders</li>
          <li><span className="text-emerald-400">GET</span> /api/v1/subscriptions — read:subscriptions</li>
          <li><span className="text-emerald-400">GET</span> /api/v1/programs — read:programs</li>
        </ul>
      </Section>

      <Section title="3 · Webhooks · verificar firma HMAC">
        <p className="text-sm text-gray-400 mb-2">
          Cada POST a tu webhook incluye estos headers:
        </p>
        <CodeBlock lang="txt" code={`X-DCC-Signature-Timestamp: 1704067200
X-DCC-Signature: sha256=<hmac>
X-DCC-Event-Id: <uuid>
X-DCC-Event-Type: enrollment.created
X-DCC-Delivery-Attempt: 1
User-Agent: DCC-Webhook-Client/1.0
Content-Type: application/json`} />
        <p className="text-sm text-gray-400 mt-4 mb-2">Verificación en <strong>Python</strong>:</p>
        <CodeBlock lang="python" code={`import hmac, hashlib, time

def verify_signature(body: bytes, headers: dict, secret: str) -> bool:
    ts = int(headers["X-DCC-Signature-Timestamp"])
    if abs(time.time() - ts) > 300:  # anti-replay 5 min
        return False
    expected = hmac.new(secret.encode(), f"{ts}.{body.decode()}".encode(), hashlib.sha256).hexdigest()
    provided = headers["X-DCC-Signature"].replace("sha256=", "")
    return hmac.compare_digest(expected, provided)`} />
        <p className="text-sm text-gray-400 mt-4 mb-2">Verificación en <strong>Node.js</strong>:</p>
        <CodeBlock lang="javascript" code={`const crypto = require("crypto");

function verifySignature(body, headers, secret) {
  const ts = parseInt(headers["x-dcc-signature-timestamp"]);
  if (Math.abs(Date.now() / 1000 - ts) > 300) return false;
  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${ts}.\${body}\`)
    .digest("hex");
  const provided = headers["x-dcc-signature"].replace("sha256=", "");
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(provided));
}`} />
        <p className="text-sm text-gray-400 mt-4 mb-2">Verificación en <strong>PHP</strong>:</p>
        <CodeBlock lang="php" code={`<?php
function verifySignature($body, $headers, $secret) {
    $ts = intval($headers['X-DCC-Signature-Timestamp']);
    if (abs(time() - $ts) > 300) return false;
    $expected = hash_hmac('sha256', "$ts." . $body, $secret);
    $provided = str_replace('sha256=', '', $headers['X-DCC-Signature']);
    return hash_equals($expected, $provided);
}`} />
      </Section>

      <Section title="4 · Rate limits">
        <p className="text-sm text-gray-300">
          Cada key tiene su propio límite en <code className="text-brand-orange-500 font-mono">requests/minuto</code> (default 120). Si excedes, respondemos
          <code className="text-brand-orange-500 font-mono ml-1">HTTP 429</code> con header <code className="text-brand-orange-500 font-mono">Retry-After: {"<segundos>"}</code>.
        </p>
      </Section>

      <Section title="5 · Retry policy de webhooks">
        <p className="text-sm text-gray-300">
          Reintentos con backoff exponencial: <code className="font-mono text-brand-orange-500">30s → 2min → 10min → 1h → 6h</code>. Después de 5 intentos, la delivery pasa a <code className="font-mono text-red-400">dead_letter</code>.
          Puedes reintentar manualmente desde el panel.
        </p>
      </Section>

      <Section title="6 · Errores comunes">
        <ul className="text-sm text-gray-300 space-y-2 font-mono">
          <li><span className="text-red-400">401 missing_api_key</span> · falta header Authorization o el valor no empieza con <code className="text-brand-orange-500">dcc_pk_</code></li>
          <li><span className="text-red-400">401 invalid_api_key</span> · key no existe o está revocada</li>
          <li><span className="text-red-400">403 insufficient_scope</span> · la key no tiene el scope requerido</li>
          <li><span className="text-red-400">403 tenant_scope_mismatch</span> · la key pertenece a otro tenant</li>
          <li><span className="text-red-400">403 ip_not_allowed</span> · IP fuera del allowlist configurado</li>
          <li><span className="text-red-400">429 rate_limit_exceeded</span> · revisa <code className="text-brand-orange-500">Retry-After</code></li>
        </ul>
      </Section>

      <div className="p-4 rounded-lg bg-brand-orange-500/10 border border-brand-orange-500/40 mt-6">
        <p className="text-xs text-brand-orange-500 font-semibold uppercase tracking-widest">💡 Recomendación</p>
        <p className="text-sm text-gray-300 mt-1">
          Para probar webhooks sin backend, usa <a href="https://webhook.site" target="_blank" rel="noreferrer" className="text-brand-orange-500 hover:underline">webhook.site <ExternalLink className="w-3 h-3 inline" /></a> — te da una URL temporal que captura payloads.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="pb-4 border-b border-gray-800">
      <h3 className="text-lg font-bold text-white font-display mb-3">{title}</h3>
      {children}
    </section>
  );
}

function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="relative group">
      <button onClick={copy}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white transition-opacity">
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      </button>
      <pre className="p-4 bg-[#0B0F14] border border-gray-800 rounded-lg text-[11px] font-mono text-gray-300 overflow-x-auto">
        <div className="text-[9px] uppercase text-gray-500 mb-2 tracking-widest">{lang}</div>
        {code}
      </pre>
    </div>
  );
}
